// The one creative drawer (docs/CREATIVE_V2.md): every creative v2 surface opens this
// panel by creative key. Preview, download, spend and results for the header window
// (with the prior period when compare is on), 30-day trend, the ad sets it runs in
// (deduplicated), and its labels. All numbers from /api/creative/v2/creative.
import { createContext, useContext, useState, type ReactNode } from 'react'
import { authFetch, useApi } from '../lib/api'
import { useDateWindow, describeWindow, type WindowBounds } from '../lib/creativeV2'
import { Drawer, Spark, Word, Caveat } from '../kit'

export interface CreativeRef { key: string; name?: string | null }
interface Totals { spend: number; results: number; cpa: number | null }
interface Detail extends Totals {
  status: string; key: string; name: string; window: WindowBounds; results_column: string
  thumb_url: string; rep_ad_id: string; has_video: boolean; ads: number; prior: Totals | null
  placements: { adset_id: string; adset_name: string | null; campaign_name: string | null; ad_id: string; spend: number; results: number; cpa: number | null }[]
  trend: { date: string; spend: number; results: number; cpa: number | null }[]
  labels: Record<string, string | null> | null
  asana: { url: string; comments: { who: string; when: string; text: string }[] } | null
}

const Ctx = createContext<(c: CreativeRef) => void>(() => {})
export const useOpenCreative = () => useContext(Ctx)

export function CreativeDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<CreativeRef | null>(null)
  return (
    <Ctx.Provider value={setOpen}>
      {children}
      {open && <CreativeDrawer key={open.key} creative={open} onClose={() => setOpen(null)} />}
    </Ctx.Provider>
  )
}

const money = (n: number | null | undefined) => n == null ? '–' : `$${Math.round(n).toLocaleString()}`
const cpaFmt = (n: number | null | undefined) => n == null ? '–' : `$${n < 100 ? n.toFixed(2) : Math.round(n).toLocaleString()}`
export const unitLabel = (col: string | undefined) => col === 'leads' ? ['Leads', 'Cost per lead'] : ['Purchases', 'Cost per purchase']
const LABELS: [string, string][] = [['persona_target', 'persona read'], ['angle_primary', 'angle'], ['hook_type', 'hook'], ['asset_type', 'asset'], ['visual_format', 'visual format']]

function Delta({ now, before, lowerIsBetter = false }: { now: number | null; before: number | null | undefined; lowerIsBetter?: boolean }) {
  if (now == null || !before) return null
  const d = Math.round((100 * (now - before)) / before)
  const worse = lowerIsBetter ? d > 0 : d < 0
  return <span className={`kit-sub ${d === 0 ? '' : worse ? 'kit-worse' : 'kit-better'}`}>{d > 0 ? '▲' : d < 0 ? '▼' : '●'} {Math.abs(d)}% vs prior</span>
}

function CreativeDrawer({ creative, onClose }: { creative: CreativeRef; onClose: () => void }) {
  const { win, q } = useDateWindow()
  const d = useApi<Detail>(`/api/creative/v2/creative?key=${encodeURIComponent(creative.key)}&${q}`, { retry: false })
  const [video, setVideo] = useState<string | null>(null)
  const [videoErr, setVideoErr] = useState(false)
  const x = d.data?.status === 'success' ? d.data : null
  const name = x?.name || creative.name || creative.key
  const [resLabel, cpaLabel] = unitLabel(x?.results_column)
  const tenant = sessionStorage.getItem('activeTenant') || ''

  const playVideo = async () => {
    if (!x) return
    try {
      const r = await authFetch(`/api/creative/video/${tenant}/${encodeURIComponent(x.rep_ad_id)}`)
      const j = await r.json()
      if (j.url) setVideo(j.url); else setVideoErr(true)
    } catch { setVideoErr(true) }
  }

  return (
    <Drawer title={name} onClose={onClose}>
      {d.isLoading && <span className="kit-sub">Loading this creative…</span>}
      {!d.isLoading && !x && <span className="kit-sub">No delivery for this creative in the selected window. Widen the window in the header.</span>}
      {x && <>
        <div className="kit-preview">
          {video ? <video src={video} controls autoPlay muted playsInline /> : <img src={x.thumb_url} alt={`Preview of ${name}`} />}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <a className="kit-btn sm" href={x.thumb_url} download={`${x.rep_ad_id}.jpg`}>Download image</a>
          {x.has_video && !video && <button className="kit-btn sm" onClick={playVideo} disabled={videoErr}>{videoErr ? 'Video not archived yet' : 'Play video'}</button>}
          {video && <a className="kit-btn sm" href={video} target="_blank" rel="noreferrer">Download video ↗</a>}
          <Word tone="quiet">{x.ads} ad{x.ads === 1 ? '' : 's'} · one creative</Word>
        </div>
        <div className="kit-card">
          <span className="k">Performance · {describeWindow(x.window, false)}</span>
          <div className="kit-stats">
            <div><span className="k">Spend</span><br /><b>{money(x.spend)}</b><br />{win.compare && <Delta now={x.spend} before={x.prior?.spend} />}</div>
            <div><span className="k">{resLabel}</span><br /><b>{x.results.toLocaleString()}</b><br />{win.compare && <Delta now={x.results} before={x.prior?.results} />}</div>
            <div><span className="k">{cpaLabel}</span><br /><b>{cpaFmt(x.cpa)}</b><br />{win.compare && <Delta now={x.cpa} before={x.prior?.cpa} lowerIsBetter />}</div>
          </div>
          {win.compare && !x.prior && <span className="kit-sub">No delivery in the prior period: new in this window.</span>}
          <Spark data={x.trend.map(t => t.spend)} color="var(--accent)" h={34} />
          <span className="kit-sub">Daily spend, last 30 days to {x.window.end}</span>
        </div>
        <div className="kit-card">
          <span className="k">Ad sets · {x.placements.length} (the same creative, deduplicated)</span>
          {x.placements.map(p => (
            <div className="kit-row" key={`${p.adset_id}-${p.ad_id}`}>
              <span title={p.campaign_name || ''}>{p.adset_name || p.adset_id}</span>
              <span className="num">{money(p.spend)}</span><span className="num">{cpaFmt(p.cpa)}</span>
            </div>
          ))}
        </div>
        <div className="kit-card">
          <span className="k">Labels</span>
          <div className="kit-kv"><span>ad name</span><b>{name}</b></div>
          {x.labels ? LABELS.filter(([k]) => x.labels?.[k]).map(([k, l]) => <div className="kit-kv" key={k}><span>{l}</span><b>{x.labels?.[k]}</b></div>)
            : <span className="kit-sub">Not analyzed yet.</span>}
          {x.labels?.creative_summary && <p className="kit-sub">{x.labels.creative_summary}</p>}
        </div>
        {x.asana && (
          <div className="kit-card">
            <span className="k">Comments · from Asana, read-only <a href={x.asana.url} target="_blank" rel="noreferrer">open card ↗</a></span>
            {x.asana.comments.map((m, i) => <div key={i} className="kit-sub"><b>{m.who}</b> {m.when}<br />{m.text}</div>)}
          </div>
        )}
        <Caveat>Meta reports one asset per ad, so download gives that asset. Spend and {resLabel.toLowerCase()} are Meta-reported and summed across every ad set this creative runs in.</Caveat>
      </>}
    </Drawer>
  )
}
